<template>
  <div class="min-h-screen">
    <SiteHeader
      :page-name="'Project Dashboard'"
      :full-screen-editing="editing"
      :editing-title
      :project-id="project?.projectInfo.projectId"
      :draw-id="project?.projectDrawInfo.drawId"
      :title-extra
      show-report
    />
    <Transition v-bind="fadeInTransition">
      <KeepAlive>
        <DrawTimeline
          v-if="project && !editing"
          ref="timeline"
          @data-updated="dataUpdated"
          @selected="drawSelected"
          v-model:project="project"
          v-model:project-reminders="projectReminders"
          :draw-items
          :permissions
          :sourceOfFunds
          :contacts
        ></DrawTimeline>
      </KeepAlive>
    </Transition>
    <Transition v-bind="fadeInTransition">
      <Tabs
        v-if="project && !editing"
        v-model:value="activeTab"
        class="text-lg border-surface-100 border-1 border-t-0 dark:border-surface-900 rounded-b-lg shadow"
      >
        <TabList>
          <Tab v-for="(tab, index) in tabs" :value="index" v-show="tab.visible">
            <i :class="tab.icon" class="mr-2"></i>
            <span>{{ tab.label }}</span>
          </Tab>
        </TabList>
      </Tabs>
    </Transition>

    <!--    <div v-if="project == null" class="flex flex-col w-full mt-20">-->
    <!--      <ProgressSpinner style="width: 200px; height: 200px" class="fill-surface-0 dark:fill-surface-800 mx-auto" animationDuration=".5s" />-->
    <!--    </div>-->
    <div class="p-4">
      <Transition v-bind="fadeSlideTransition">
        <ProjectInfoTab
          ref="projectInfoTab"
          v-if="activeTab === 0 && project"
          v-model:project="project"
          v-model:draw-items="drawItems"
          v-model:contacts="contacts"
          v-model:currentFlexFields="currentFlexFields"
          v-model:editing="editing"
          v-model:editing-title="editingTitle"
          :can-edit
          :permissions
          :source-of-funds
          @project-info-updated="dataUpdated"
        ></ProjectInfoTab>
        <DrawInformation
          v-else-if="activeTab === 1 && project"
          v-model:project="project"
          v-model:editing="editing"
          v-model:editing-title="editingTitle"
          v-model:draw-items="drawItems"
          v-model:source-of-funds="sourceOfFunds"
          v-model:projectReminders="projectReminders"
          :prev-project-header
          :permissions
          :can-edit
          :is-current-draw="isCurrentDraw"
          :entities
          :contacts
          @data-updated="dataUpdated"
          @update-timeline-name="updateTimelineName"
        ></DrawInformation>
        <BudgetTab
          ref="budgetTab"
          v-else-if="activeTab === 2 && project"
          v-model:project="project"
          v-model:draw-items="drawItems"
          v-model:source-of-funds="sourceOfFunds"
          v-model:editing="editing"
          v-model:editing-title="editingTitle"
          v-model:project-reminders="projectReminders"
          :entities
          :contacts
          :can-edit
          :permissions
          @budget-updated="dataUpdated"
        ></BudgetTab>
        <DrawPayments ref="ledgerTab" v-else-if="activeTab === 3 && project" v-model:project="project" :permissions></DrawPayments>
        <ReminderManager
          v-else-if="activeTab === 4 && project"
          v-model:projectReminders="projectReminders"
          v-model:reminders="reminders"
          :project-id="project.projectInfo.projectId"
          :can-edit
        >
        </ReminderManager>
        <ChecklistManager
          v-else-if="activeTab === 5 && project"
          ref="checklistManager"
          v-model:checklists="checklists"
          v-model:checklistTemplates="checklistTemplates"
          v-model:editing="editing"
          v-model:editing-title="editingTitle"
          :project="project"
          :can-edit
          :draw-items="drawItems"
        ></ChecklistManager>
        <ActivityLog
          v-else-if="activeTab === 6 && project"
          ref="activityLog"
          v-model:project="project"
          v-model:correspondence-entries="correspondenceEntries"
          v-model:activityLogContacts="activityLogContacts"
          v-model:reached-allocated-inspections="activityLogReachedAllocatedInspections"
          v-model:activity-log-entries="activityLogEntries"
          :prev-project-header
          :permissions
          :is-current-draw="isCurrentDraw"
          :contacts
          @data-updated="dataUpdated"
        ></ActivityLog>
        <UnitsTab v-else-if="activeTab === 7 && propsStore.demo && project" :entities :project :currentFlexFields :sourceOfFunds></UnitsTab>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProjectDashboardSchema, type Project, type ProjectDrawInfo } from "@/types/Projects";
import { type DrawItem } from "@/types/DrawItem";
import { type ProjectEntity } from "@/types/ProjectEntity";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import { type FlexRowField } from "@/types/FlexRows";
import { type Checklist, ChecklistListSchema } from "@/types/Checklist";
import { type Reminder, type ProjectReminder, ReminderListSchema } from "@/types/Reminder";
import { checkBudgetBasedReminders, checkDrawReminders } from "@/services/ReminderService";
import { useConfirm } from "primevue/useconfirm";
import ActivityLog from "@/components/project-dashboard/activity-log/ActivityLog.vue";
import type { CorrespondenceEntry, ActivityLogContact, Activity } from "@/types/ActivityLog";
import { fadeInTransition, fadeSlideTransition } from "@/services/Transitions";
import type { Entity } from "@/types/Entity";
import { getAllEntities } from "@/services/EntitiesService";
import { usePropsStore } from "@/stores/props";
import { useSystemStore } from "@/stores/systemStore";

const route = useRoute();
const confirm = useConfirm();
const systemStore = useSystemStore();

const activeTab = ref(0);
const project = ref<Project>();
const prevProjectHeader = ref<ProjectDrawInfo | null>(null);
const drawItems = ref<DrawItem[]>([]);
const sourceOfFunds = ref<ProjectEntity[]>([]);
const contacts = ref<ProjectEntity[]>([]);
const isCurrentDraw = ref(true);
const currentFlexFields = ref<FlexRowField[]>([]);
const checklists = ref<Checklist[]>([]);
const checklistTemplates = ref<Checklist[]>([]);
const projectReminders = ref<ProjectReminder[]>([]);
const reminders = ref<Reminder[]>([]);
const correspondenceEntries = ref<CorrespondenceEntry[]>([]);
const activityLogContacts = ref<ActivityLogContact[]>([]);
const activityLogReachedAllocatedInspections = ref<boolean>(false);
const activityLogEntries = ref<Activity[]>([]);
const entities = ref<Entity[]>([]);

const checklistManager = ref();

const permissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));

const titleExtra = ref<string>("");
const editing = ref(false);
const editingTitle = ref("");

const hasChecking = computed<boolean>(() => project.value?.projectInfo.checkingAccount != null);

const canEdit = ref(false);

const budgetTab = ref();
const projectInfoTab = ref();
const ledgerTab = ref();

const propsStore = usePropsStore();

const tabs = ref([
  { label: "Project Info", icon: "pi pi-building-columns", visible: true },
  { label: "Draw Information", icon: "pi pi-info-circle", visible: true },
  { label: "Budget", icon: "pi pi-money-bill", visible: true },
  { label: "Payments", icon: "pi pi-dollar", visible: hasChecking },
  { label: "Notifications/Reminders", icon: "pi pi-bell", visible: true },
  { label: "Checklists", icon: "pi pi-check-circle", visible: true },
  { label: "Activity Log", icon: "pi pi-list", visible: true },
  { label: "Units", icon: "pi pi-building", visible: propsStore.demo },
]);

const timeline = ref();
const updateTimelineName = () => {
  timeline.value.updateDrawName(project.value?.projectDrawInfo.drawNumber, project.value?.projectDrawInfo.name);
};

//TODO: Not sure we need to grab EVERYTHING again
const dataUpdated = () => {
  if (project.value != null) getProject(project.value.projectDrawInfo.drawNumber, false);
  if (projectInfoTab.value) projectInfoTab.value.getDisbursementHistoryData();
};

const drawSelected = (drawNumber: number, isCurrent: boolean): void => {
  getProject(drawNumber, false);
  isCurrentDraw.value = isCurrent;
  checkAllowedEditing();
};

//TODO: Figure out if multi draw/ interest draw is a client specific thing

const checkAllowedEditing = (): void => {
  if (project.value != null && isCurrentDraw.value) {
    canEdit.value =
      permissions.value.administrator ||
      permissions.value.drawAssistant ||
      permissions.value.editProject ||
      (permissions.value.addProject && project.value.projectDrawInfo.drawNumber === 0);
  } else {
    canEdit.value = false;
  }
};

const getProject = async (drawNumber: number | null, firstLoad: boolean): Promise<void> => {
  let url = "/projects/dashboard/" + route.params.id;
  if (drawNumber != null) url += "?drawNumber=" + drawNumber;

  if (firstLoad) systemStore.startLoading("Loading Project...");

  await axios
    .get(url)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          const projectDashboard = ProjectDashboardSchema.parse(data);

          project.value = projectDashboard.project;
          drawItems.value = projectDashboard.drawItems;
          projectReminders.value = projectDashboard.projectReminders;
          prevProjectHeader.value = projectDashboard.prevProjectHeader;
          sourceOfFunds.value = projectDashboard.sourceOfFunds;
          currentFlexFields.value = projectDashboard.currentFlexFields;
          checklists.value = projectDashboard.checklists;
          contacts.value = projectDashboard.contacts;
          permissions.value = projectDashboard.permissions;
          correspondenceEntries.value = projectDashboard.activityLog.entries;
          activityLogContacts.value = projectDashboard.activityLog.contacts;
          activityLogReachedAllocatedInspections.value = projectDashboard.activityLog.reachedAllocatedInspectionRequests;
          activityLogEntries.value = projectDashboard.activityLog.activityLog;

          if (project.value.projectDrawInfo.inspectionDate != null && project.value.projectDrawInfo.inspectionDate.getFullYear() === 1900)
            project.value.projectDrawInfo.inspectionDate = null;

          setTimeout(() => {
            if (checklistManager.value) checklistManager.value.updateTabInfo();
            if (budgetTab.value) budgetTab.value.condensedBudgetTable.reloadTable();
            if (ledgerTab.value) ledgerTab.value.reloadDraw();
          }, 10);

          if (firstLoad) {
            checkBudgetBasedReminders(
              confirm,
              projectReminders.value,
              drawItems.value,
              project.value.projectDrawInfo.percentComplete,
              project.value.projectDrawInfo.percentDrawn,
              project.value.projectInfo.processor.entityId
            );
            checkDrawReminders(
              confirm,
              projectReminders.value,
              project.value.projectDrawInfo.drawNumber,
              project.value.projectInfo.processor.entityId
            );
          }

          checkAllowedEditing();
          if (!canEdit && !permissions.value.printReports)
            info(
              "Actions Restricted",
              "You do not have permission to edit projects.\nTo gain access to this feature, please contact your system administrator.",
              5000
            );
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e))
    .finally(() => systemStore.stopLoading());

  getAllEntities().then((result: Entity[]) => (entities.value = result));
  if (project.value) {
    titleExtra.value = " for " + project.value.projectInfo.projectName + " (Draw " + project.value.projectDrawInfo.drawNumber + ")";
  }
};

onMounted(async (): Promise<void> => {
  getProject(null, true);

  axios
    .get("/checklist/templates")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(ChecklistListSchema, data, checklistTemplates);
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));

  axios.get("/reminder").then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        parseData(ReminderListSchema, data, reminders);
      },
      (errors: ApiError[]) => {
        error("An error occurred", errors[0].message);
      }
    );
  });
});
</script>
